import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Clock, TrendingUp, Timer, Flame, AlertCircle, ChevronLeft, ChevronRight, FileEdit } from 'lucide-react';
import { useAttendanceHistory } from '@/hooks/useAttendanceHistory';
import { useMyStrikes } from '@/hooks/useMyStrikes';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, startOfDay, isAfter } from 'date-fns';
import { formatTimeIST, formatDateIST } from '@/utils/dateUtils';
import StrikeSummaryCard from './StrikeSummaryCard';
import StrikeWarningBanner from './StrikeWarningBanner';
import StrikeHistoryModal from './StrikeHistoryModal';
import DailyRegularizationModal from './DailyRegularizationModal';

/**
 * StatusBadge component for displaying attendance status
 */
function StatusBadge({ status }) {
  const styles = {
    on_time: 'bg-green-100 text-green-800',
    present: 'bg-green-100 text-green-800',
    half_day: 'bg-amber-100 text-amber-800',
    late: 'bg-amber-100 text-amber-800',
    early_checkout: 'bg-orange-100 text-orange-800',
    missed: 'bg-red-100 text-red-800',
    absent: 'bg-red-50 text-red-600',
    checked_in: 'bg-blue-100 text-blue-800',
    regularized: 'bg-blue-100 text-blue-800',
    weekly_off: 'bg-gray-100 text-gray-600'
  };
  const labels = {
    on_time: 'On Time',
    present: 'Present',
    half_day: 'Half Day',
    checked_in: 'Checked In',
    weekly_off: 'Weekly Off',
    late: 'Late',
    early_checkout: 'Early',
    missed: 'Missed',
    absent: 'Absent',
    regularized: 'Regularized'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels[status] || status || 'Unknown'}
    </span>
  );
}

/**
 * SummaryCard component for displaying a single metric
 */
function SummaryCard({ icon: Icon, label, value, className = '' }) {
  return (
    <Card className={className}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

/**
 * AttendanceHistory component - displays attendance history with summary and filtering
 */
export default function AttendanceHistory() {
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [showStrikeHistory, setShowStrikeHistory] = useState(false);
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [regDate, setRegDate] = useState(null);

  // Fetch strike data
  const { strikes, loading: strikesLoading } = useMyStrikes();

  const {
    entries,
    summary,
    loading,
    error,
    totalCount,
    filters,
    updateFilters,
    setPage
  } = useAttendanceHistory();

  // Generate absent-day placeholders for past dates with no attendance record
  const allEntries = React.useMemo(() => {
    if (!filters.from_date || !filters.to_date) return entries;
    try {
      const from = parseISO(filters.from_date);
      const to = parseISO(filters.to_date);
      const today = startOfDay(new Date());
      const endDate = isBefore(to, today) ? to : new Date(today.getTime() - 86400000); // yesterday or to_date
      if (isAfter(from, endDate)) return entries;

      const existingDates = new Set(entries.map(e => e.date?.substring(0, 10)));
      const allDays = eachDayOfInterval({ start: from, end: endDate });
      const absentDays = allDays
        .filter(d => !existingDates.has(format(d, 'yyyy-MM-dd')))
        .map(d => ({
          id: `absent-${format(d, 'yyyy-MM-dd')}`,
          date: format(d, 'yyyy-MM-dd'),
          location_name: null,
          check_in_time: null,
          check_out_time: null,
          hours_worked: null,
          status: 'absent',
          check_in_photo_url: null,
          regularized: false,
          _isAbsentPlaceholder: true,
        }));

      const merged = [...entries, ...absentDays].sort((a, b) => b.date.localeCompare(a.date));
      return filters.status ? entries : merged; // Only show absent rows when "All Statuses" filter
    } catch { return entries; }
  }, [entries, filters.from_date, filters.to_date, filters.status]);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / filters.page_size);
  const hasNextPage = filters.page < totalPages;
  const hasPrevPage = filters.page > 1;

  // Handle "This Month" button
  const handleThisMonth = () => {
    const now = new Date();
    updateFilters({
      from_date: format(startOfMonth(now), 'yyyy-MM-dd'),
      to_date: format(endOfMonth(now), 'yyyy-MM-dd')
    });
  };

  // Format time for display (IST timezone)
  const formatTime = (isoString) => {
    return formatTimeIST(isoString) || '-';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#891d33] mx-auto mb-4"></div>
        Loading attendance history...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-300" />
        <p className="font-medium">Failed to load attendance history</p>
        <p className="text-sm text-gray-500 mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Strike Warning Banner - shows only if 2+ strikes */}
      {strikes && (
        <StrikeWarningBanner
          strikeCount={strikes.strike_count}
          isLocked={strikes.is_locked}
        />
      )}

      {/* Summary Dashboard Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        {/* Strike Card - first position */}
        {strikes && (
          <StrikeSummaryCard
            strikeCount={strikes.strike_count}
            maxStrikes={strikes.max_strikes}
            isLocked={strikes.is_locked}
            onClick={() => setShowStrikeHistory(true)}
          />
        )}

        <SummaryCard
          icon={Clock}
          label="Hours This Month"
          value={`${summary?.total_hours_month?.toFixed(1) || '0'}h`}
        />
        <SummaryCard
          icon={TrendingUp}
          label="Attendance Rate"
          value={`${summary?.attendance_rate?.toFixed(0) || '0'}%`}
        />
        <SummaryCard
          icon={Timer}
          label="Avg Check-in"
          value={summary?.avg_check_in_time || '-'}
        />
        <SummaryCard
          icon={Flame}
          label="Streak"
          value={`${summary?.current_streak || 0} days`}
        />
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Date range */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.from_date}
            onChange={(e) => updateFilters({ from_date: e.target.value })}
            className="border rounded px-2 py-1.5 text-sm w-[140px]"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={filters.to_date}
            onChange={(e) => updateFilters({ to_date: e.target.value })}
            className="border rounded px-2 py-1.5 text-sm w-[140px]"
          />
        </div>

        {/* Status filter */}
        <select
          value={filters.status || ''}
          onChange={(e) => updateFilters({ status: e.target.value || null })}
          className="border rounded px-2 py-1.5 text-sm min-w-[130px]"
        >
          <option value="">All Statuses</option>
          <option value="present">Present</option>
          <option value="half_day">Half Day</option>
          <option value="checked_in">Checked In</option>
          <option value="weekly_off">Weekly Off</option>
          <option value="regularized">Regularized</option>
        </select>

        {/* This Month preset */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleThisMonth}
          className="h-8"
        >
          This Month
        </Button>
      </div>

      {/* History Table */}
      {allEntries.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-gray-500 bg-gray-50">
          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium text-gray-600">No attendance records found</p>
          <p className="text-sm mt-2">
            {filters.status
              ? `No ${filters.status.replace('_', ' ')} records in the selected date range.`
              : 'No attendance records in the selected date range.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Date</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Check In</TableHead>
                  <TableHead>Check Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Photo</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allEntries.map((entry) => (
                  <TableRow key={entry.id || entry.date + entry.location_name}>
                    <TableCell className="font-medium">
                      {formatDate(entry.date)}
                    </TableCell>
                    <TableCell>{entry.location_name || '-'}</TableCell>
                    <TableCell>{formatTime(entry.check_in_time)}</TableCell>
                    <TableCell>{formatTime(entry.check_out_time)}</TableCell>
                    <TableCell>
                      {entry.hours_worked != null
                        ? `${entry.hours_worked.toFixed(1)}h`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={entry.status} />
                    </TableCell>
                    <TableCell>
                      {entry.check_in_photo_url ? (
                        <img
                          src={entry.check_in_photo_url}
                          alt="Check-in"
                          className="w-10 h-10 rounded object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setPreviewPhoto(entry.check_in_photo_url)}
                        />
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(entry.status === 'missed' || entry.status === 'absent' || (!entry.check_in_time && !entry.regularized)) && entry.status !== 'weekly_off' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs"
                          onClick={() => {
                            setRegDate(entry.date);
                            setRegModalOpen(true);
                          }}
                        >
                          <FileEdit className="h-3 w-3 mr-1" />
                          Regularize
                        </Button>
                      )}
                      {entry.regularized && (
                        <span className="text-xs text-blue-600 font-medium">Regularized</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {entries.map((entry) => (
              <Card key={entry.id || entry.date + entry.location_name} className="overflow-hidden">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium">{formatDate(entry.date)}</div>
                      <div className="text-sm text-gray-500">{entry.location_name || '-'}</div>
                    </div>
                    <StatusBadge status={entry.status} />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div>
                      <span className="text-gray-400">In: </span>
                      {formatTime(entry.check_in_time)}
                    </div>
                    <div>
                      <span className="text-gray-400">Out: </span>
                      {formatTime(entry.check_out_time)}
                    </div>
                    <div>
                      <span className="text-gray-400">Hours: </span>
                      {entry.hours_worked != null ? `${entry.hours_worked.toFixed(1)}h` : '-'}
                    </div>
                  </div>
                  {entry.check_in_photo_url && (
                    <div className="mt-2 pt-2 border-t">
                      <img
                        src={entry.check_in_photo_url}
                        alt="Check-in"
                        className="w-12 h-12 rounded object-cover cursor-pointer hover:opacity-80"
                        onClick={() => setPreviewPhoto(entry.check_in_photo_url)}
                      />
                    </div>
                  )}
                  {(entry.status === 'missed' || entry.status === 'absent' || (!entry.check_in_time && !entry.regularized)) && entry.status !== 'weekly_off' && (
                    <div className="mt-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs w-full"
                        onClick={() => {
                          setRegDate(entry.date);
                          setRegModalOpen(true);
                        }}
                      >
                        <FileEdit className="h-3 w-3 mr-1" />
                        Request Regularization
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-500">
                Page {filters.page} of {totalPages} ({totalCount} records)
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(filters.page - 1)}
                  disabled={!hasPrevPage}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(filters.page + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewPhoto(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]">
            <img
              src={previewPhoto}
              alt="Attendance photo"
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            />
            <button
              className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 text-gray-700 shadow-lg"
              onClick={() => setPreviewPhoto(null)}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Strike History Modal */}
      {strikes && (
        <StrikeHistoryModal
          open={showStrikeHistory}
          onOpenChange={setShowStrikeHistory}
          strikeData={strikes}
        />
      )}

      {/* Regularization Modal (NEW two-step system with dual proof) */}
      <DailyRegularizationModal
        isOpen={regModalOpen}
        onClose={() => setRegModalOpen(false)}
        selectedDate={regDate}
        onSuccess={() => {
          setRegModalOpen(false);
          // Refresh the history after successful regularization request
          updateFilters({ ...filters });
        }}
      />
    </div>
  );
}
